/**
 * RL Loop Export Modal
 *
 * Modal for exporting RL Loop results in various formats (PDF, Markdown, JSON).
 * Allows users to customize what content is included in the export.
 */

import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Divider,
  Chip
} from '@mui/material';
import {
  Download as DownloadIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as CostIcon
} from '@mui/icons-material';
import { exportRLLoop } from '../../../services/rlLoopApi';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 600 },
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
  overflow: 'auto'
};

const RLLoopExportModal = ({ open, onClose, loopId, token, loop }) => {
  // State for export configuration
  const [config, setConfig] = useState({
    format: 'pdf',
    includeTranscripts: false,
    includePromptChanges: true,
    includeMetrics: true,
    includeReviews: true,
    includeCosts: true
  });

  // State for export
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  const handleConfigChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleExport = async () => {
    setDownloading(true);
    setError(null);

    try {
      const response = await exportRLLoop(loopId, config, token);
      const loopName = loop?.name?.replace(/[^a-z0-9]/gi, '-') || 'rl-loop';
      const timestamp = Date.now();

      if (config.format === 'pdf') {
        const blob = new Blob([response], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${loopName}-${timestamp}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else if (config.format === 'markdown') {
        const blob = new Blob([response], { type: 'text/markdown' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${loopName}-${timestamp}.md`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else if (config.format === 'json') {
        const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${loopName}-${timestamp}.json`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }

      // Close modal after successful download
      onClose();
    } catch (error) {
      console.error('Error exporting RL Loop:', error);
      setError('Failed to export RL Loop. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  // Extract metadata from loop
  const iterationCount = loop?.progress?.currentIteration || 0;
  const status = loop?.status || 'unknown';
  const hasMetrics = loop?.metrics?.qualityTrend?.length > 0;
  const hasPromotions = (loop?.promotionRecords || []).length > 0;
  const qualityImprovement = loop?.metrics?.currentQuality && loop?.metrics?.startingQuality
    ? ((loop.metrics.currentQuality - loop.metrics.startingQuality) / loop.metrics.startingQuality * 100).toFixed(1)
    : null;

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'running': return 'info';
      case 'paused': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h5" component="h2" gutterBottom>
          Export RL Loop Report
        </Typography>

        {loop && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {loop.name}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip
                label={status.toUpperCase()}
                size="small"
                color={getStatusColor(status)}
              />
              <Chip
                icon={<CheckCircleIcon />}
                label={`${iterationCount} iterations`}
                size="small"
                color="primary"
              />
              {hasMetrics && qualityImprovement && (
                <Chip
                  icon={<TrendingUpIcon />}
                  label={`${qualityImprovement}% improvement`}
                  size="small"
                  color={parseFloat(qualityImprovement) > 0 ? 'success' : 'warning'}
                  variant="outlined"
                />
              )}
              {hasPromotions && (
                <Chip
                  label="Has promotions"
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Format Selection */}
          <FormControl fullWidth>
            <InputLabel>Export Format</InputLabel>
            <Select
              value={config.format}
              label="Export Format"
              onChange={handleConfigChange('format')}
            >
              <MenuItem value="pdf">
                <Box display="flex" alignItems="center" gap={1}>
                  PDF Document
                  <Typography variant="caption" color="text.secondary">
                    (best for sharing)
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="markdown">
                <Box display="flex" alignItems="center" gap={1}>
                  Markdown
                  <Typography variant="caption" color="text.secondary">
                    (readable text format)
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem value="json">
                <Box display="flex" alignItems="center" gap={1}>
                  JSON
                  <Typography variant="caption" color="text.secondary">
                    (for data analysis)
                  </Typography>
                </Box>
              </MenuItem>
            </Select>
          </FormControl>

          <Divider />

          {/* Content Options */}
          <Typography variant="subtitle1" fontWeight="bold">
            Content to Include
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeMetrics}
                onChange={handleConfigChange('includeMetrics')}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <TrendingUpIcon fontSize="small" color="action" />
                Quality Metrics
                <Typography variant="caption" color="text.secondary">
                  (quality scores, trends, improvement rates)
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeReviews}
                onChange={handleConfigChange('includeReviews')}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                Iteration Reviews
                <Typography variant="caption" color="text.secondary">
                  (evaluations, safety flags)
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includePromptChanges}
                onChange={handleConfigChange('includePromptChanges')}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                Prompt Changes
                <Typography variant="caption" color="text.secondary">
                  (applied improvements per iteration)
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeCosts}
                onChange={handleConfigChange('includeCosts')}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                <CostIcon fontSize="small" color="action" />
                Cost Information
                <Typography variant="caption" color="text.secondary">
                  (token usage, API costs)
                </Typography>
              </Box>
            }
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={config.includeTranscripts}
                onChange={handleConfigChange('includeTranscripts')}
              />
            }
            label={
              <Box display="flex" alignItems="center" gap={1}>
                Simulation Transcripts
                <Typography variant="caption" color="text.secondary">
                  (full conversation logs - large files)
                </Typography>
              </Box>
            }
          />

          <Divider />

          {/* Info Alerts */}
          {config.includeTranscripts && (
            <Alert severity="info" icon={<InfoIcon />}>
              Including transcripts will significantly increase file size. Each iteration may contain multiple simulation transcripts.
            </Alert>
          )}

          {config.format === 'pdf' && (
            <Alert severity="info" icon={<InfoIcon />}>
              PDF format is optimized for sharing with researchers. It includes professional formatting with tables, charts references, and page numbers.
            </Alert>
          )}

          {/* Action Buttons */}
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button onClick={onClose} disabled={downloading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleExport}
              disabled={downloading}
              startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
            >
              {downloading ? 'Exporting...' : `Export as ${config.format.toUpperCase()}`}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Modal>
  );
};

export default RLLoopExportModal;
